(function () {
  const STORAGE_PREFIX = 'halfdays:region-notice-dismissed';
  const DETECTION_CACHE = 'halfdays:region-notice-detected';

  // Shopify's own geolocation endpoint, the one its Geolocation app uses
  function detectCountry() {
    const cached = readDetectionCache();
    if (cached !== null) return Promise.resolve(cached);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(`${root}browsing_context_suggestions.json?source=geolocation_recommendation`, {
      headers: { Accept: 'application/json' }
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        const detected = payload && payload.detected_values && payload.detected_values.country;
        const code = detected && (detected.handle || detected.code || detected.iso_code);
        const value = code ? String(code).toUpperCase() : '';
        writeDetectionCache(value);
        return value;
      })
      .catch(() => '');
  }

  function readDetectionCache() {
    try {
      return window.sessionStorage.getItem(DETECTION_CACHE);
    } catch (error) {
      return null;
    }
  }

  function writeDetectionCache(value) {
    try {
      window.sessionStorage.setItem(DETECTION_CACHE, value);
    } catch (error) {
      /* private mode: just detect again next page view */
    }
  }

  class RegionNotice extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.currentCountry = (this.getAttribute('current-country-code') || '').toUpperCase();
      this.dismissDays = parseInt(this.getAttribute('dismiss-days'), 10) || 30;
      this.countries = this.readCountries();

      this.querySelectorAll('[data-region-notice-dismiss]').forEach((button) => {
        button.addEventListener('click', () => this.dismiss('close'));
      });

      this.querySelectorAll('[data-region-notice-stay]').forEach((button) => {
        button.addEventListener('click', () => this.stay());
      });

      const confirmButton = this.querySelector('[data-region-notice-confirm]');
      if (confirmButton) {
        confirmButton.addEventListener('click', () => this.remember());
      }

      this.guardCountryInput();
      this.evaluate();
    }

    guardCountryInput() {
      const form = this.querySelector('form');
      if (!form) return;

      form.addEventListener('submit', () => {
        const input = this.querySelector('[data-region-notice-country-input]');

        // theme.js:8406 writes every country_code input on the page when the old
        // footer/location-popup selectors are used, so re-assert ours before sending.
        if (input && this.detectedCountry) input.value = this.detectedCountry;
      });
    }

    readCountries() {
      try {
        const script = this.querySelector('[data-region-notice-countries]');
        return script ? JSON.parse(script.textContent) : {};
      } catch (error) {
        return {};
      }
    }

    evaluate() {
      const forced = this.forcedCountry();
      if (forced) {
        this.reveal(forced);
        return;
      }

      if (this.isDismissed()) return;

      detectCountry().then((detected) => {
        if (!detected) return;
        if (detected === this.currentCountry) return;
        // Only offer markets the store can actually sell to.
        if (!this.countries[detected]) return;

        this.reveal(detected);
      });
    }

    reveal(countryCode) {
      const name = this.countries[countryCode] || countryCode;

      const text = this.querySelector('[data-region-notice-text]');
      if (text) text.innerHTML = (text.dataset.template || text.innerHTML).split('[country]').join(name);

      const confirmButton = this.querySelector('[data-region-notice-confirm]');
      if (confirmButton) {
        confirmButton.textContent = (confirmButton.dataset.template || confirmButton.textContent)
          .split('[country]')
          .join(name);
      }

      const input = this.querySelector('[data-region-notice-country-input]');
      if (input) input.value = countryCode;

      this.detectedCountry = countryCode;
      if (text) this.setAttribute('aria-label', text.textContent.replace(/\s+/g, ' ').trim());
      this.hidden = false;
    }

    get storageKey() {
      // Scoped to the host so each market domain keeps its own dismissal.
      return `${STORAGE_PREFIX}:${window.location.hostname}:${this.currentCountry}`;
    }

    get storage() {
      try {
        const probe = '__hd__';
        window.localStorage.setItem(probe, probe);
        window.localStorage.removeItem(probe);
        return window.localStorage;
      } catch (error) {
        return null;
      }
    }

    // QA hook: ?region_notice=preview picks a market other than the current one,
    // ?region_notice=<iso> forces a specific country. The theme editor previews too.
    forcedCountry() {
      const param = new URLSearchParams(window.location.search).get('region_notice');
      const inEditor = !!(window.Shopify && window.Shopify.designMode);

      if (!param && !inEditor) return '';

      if (param && param !== 'preview') {
        return param.toUpperCase();
      }

      const other = Object.keys(this.countries).find((code) => code !== this.currentCountry);
      return other || '';
    }

    isDismissed() {
      const store = this.storage;
      if (!store) return false;

      const expiresAt = parseInt(store.getItem(this.storageKey), 10);
      if (isNaN(expiresAt)) return false;

      if (Date.now() > expiresAt) {
        store.removeItem(this.storageKey);
        return false;
      }

      return true;
    }

    remember() {
      const store = this.storage;
      if (!store) return;
      store.setItem(this.storageKey, String(Date.now() + this.dismissDays * 86400000));
    }

    reset() {
      const store = this.storage;
      if (store) store.removeItem(this.storageKey);
      try {
        window.sessionStorage.removeItem(DETECTION_CACHE);
      } catch (error) {
        /* nothing cached to clear */
      }
      this.evaluate();
    }

    dismiss(reason) {
      this.remember();
      this.hidden = true;
      this.dispatchEvent(
        new CustomEvent('region-notice:dismissed', {
          bubbles: true,
          detail: { reason, countryCode: this.detectedCountry, days: this.dismissDays }
        })
      );
    }

    stay() {
      // Cancelable so other scripts can take over the "stay" action.
      const event = new CustomEvent('region-notice:stay', {
        bubbles: true,
        cancelable: true,
        detail: { countryCode: this.detectedCountry, days: this.dismissDays }
      });

      if (!this.dispatchEvent(event)) return;

      this.dismiss('stay');
    }
  }

  if (!customElements.get('region-notice')) {
    customElements.define('region-notice', RegionNotice);
  }
})();
